import axios from "axios";
import { API_BASE_URL } from "../constants/api.constants";

const ENDPOINT = `${API_BASE_URL}/ClientLog`;
const MAX_PER_SESSION = 50;
const DEDUP_WINDOW_MS = 60000;

// Browser/extension/transient noise that isn't our bug — never report.
const DENYLIST = [
  "ResizeObserver loop",
  "Script error.",
  "Non-Error promise rejection captured",
  "Unable to load image data:image/svg+xml",
  "Cannot read properties of undefined (reading 'M_ID')",
  "Failed to connect to MetaMask",
];

let appVersion = null;
let reportCount = 0;
const recentKeys = new Map();

function loadAppVersion() {
  const url = `${process.env.PUBLIC_URL || ""}/meta.json`;
  axios
    .get(url)
    .then((res) => {
      appVersion = res.data?.version ?? null;
    })
    .catch(() => {});
}

function isDenied(message) {
  return DENYLIST.some((pattern) => message.includes(pattern));
}

function isThrottled(message, stackRaw) {
  const key = `${message}|${(stackRaw || "").split("\n")[1] || ""}`;
  const now = Date.now();
  const last = recentKeys.get(key);
  if (last && now - last < DEDUP_WINDOW_MS) {
    return true;
  }
  recentKeys.set(key, now);
  return false;
}

function report(message, stackRaw, componentStack) {
  try {
    const token = localStorage.getItem("token");
    // No token = logged out / pre-auth. The [Authorize] endpoint would 401, so
    // don't send a doomed request (pre-auth reporting needs the deferred
    // anonymous endpoint, not a tokenless POST here).
    if (!token) {
      return;
    }

    if (!message || isDenied(message)) {
      return;
    }
    if (reportCount >= MAX_PER_SESSION || isThrottled(message, stackRaw)) {
      return;
    }
    reportCount++;

    // Lean POST on bare axios (NOT the configured instance) so its 401-redirect
    // interceptor can't fire on a fire-and-forget log call. .catch swallows the
    // outcome so a failed log post never becomes an unhandled rejection (loop).
    axios
      .post(
        ENDPOINT,
        {
          message: String(message).slice(0, 2000),
          stackRaw: stackRaw ? String(stackRaw).slice(0, 8000) : null,
          componentStack: componentStack
            ? String(componentStack).slice(0, 8000)
            : null,
          url: window.location.href,
          appVersion,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .catch(() => {});
  } catch {
    // Reporting must never throw — that could re-trigger the handlers.
  }
}

export function reportComponentError(error, componentStack) {
  report(error?.message ?? String(error), error?.stack ?? null, componentStack);
}

export function installGlobalErrorHandlers() {
  loadAppVersion();

  window.addEventListener("error", (event) => {
    report(
      event?.message ?? event?.error?.message ?? "Unknown error",
      event?.error?.stack ?? null,
      null,
    );
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    report(reason?.message ?? String(reason), reason?.stack ?? null, null);
  });
}
